import './UserDetailPage.css';
import { useContext, useEffect } from 'react';
import PageScaffold from '../../components/PageScaffold';
import UserCreationForm from '../../components/user_detail_page/UserCreationForm';
import { USERS_PAGE_PATH } from './UsersPage';
import { useNavigate, useParams } from 'react-router-dom';
import UsersContext from '../../contexts/data/UsersContext';
import { Card } from 'antd';
import { UserCreationFormProvider } from '../../contexts/forms/users/UserCreationFormContext';
import { UserEditionFormProvider } from '../../contexts/forms/users/UserEditionFormContext';
import PlansContext from '../../contexts/data/PlansContext';
import UserDetailStatsContext from '../../contexts/data/UserDetailStatsContext';
import StatsTile from '../../components/@shared/StatsTile';
import { formatNumber } from '@leedpulse-monorepo/utils';
import { ContextStatus } from '@leedpulse-monorepo/enums';
import UserTabs from '../../components/user_detail_page/UserTabs';
import { BellOutlined, ContactsOutlined } from '@ant-design/icons';

interface UserDetailPageProps {
  isCreateMode: boolean;
}

const UserDetailPage: React.FC<UserDetailPageProps> = ({ isCreateMode }) => {
  const usersContext = useContext(UsersContext);
  const plansContext = useContext(PlansContext);
  const userDetailStatsContext = useContext(UserDetailStatsContext);
  const navigate = useNavigate();

  const { userId } = useParams<{ userId: string }>();

  const selectionMismatch =
    (!isCreateMode && usersContext.selected === null) ||
    (!isCreateMode && userId && usersContext.selected?.uid !== userId);

  const statsItems = [
    {
      icon: <ContactsOutlined style={{ marginRight: 6 }} />,
      title: 'Contacts',
      value: formatNumber(userDetailStatsContext.contactsCount ?? 0)
    },
    {
      icon: <BellOutlined style={{ marginRight: 6 }} />,
      title: 'Notifications',
      value: formatNumber(userDetailStatsContext.notificationsCount ?? 0)
    }
  ]

  useEffect(() => {
    if (
      selectionMismatch &&
      usersContext.selectionStatus !== ContextStatus.LOADING &&
      userId
    ) {
      usersContext.fetchAndSelectById(userId);
    }
    return () => {
      usersContext.reset();
    };
  }, []);

  useEffect(() => {
    if (plansContext.status !== ContextStatus.SUCCESS) {
      plansContext.fetch();
    }
  }, []);

  useEffect(() => {
    if (!isCreateMode && userId) {
      userDetailStatsContext.fetch(userId);
    }
  }, [])

  if (!userId && !isCreateMode) {
    navigate(USERS_PAGE_PATH);
    return;
  }

  return (
    <PageScaffold
      title={
        isCreateMode
          ? 'Nouveau compte'
          : usersContext.selected
            ? usersContext.selected?.firstname +
            ' ' +
            usersContext.selected?.lastname
            : ''
      }
      subtitle={
        isCreateMode
          ? 'Créez un nouveau compte sur Leedpulse'
          : "Consultez et modifiez les informations de l'utilisateur"
      }
      breadcrumbItems={[
        {
          target: USERS_PAGE_PATH,
          title: 'Utilisateurs',
        },

        {
          title: isCreateMode ? 'Nouveau' : (usersContext.selected?.firstname || '') + ' ' + (usersContext.selected?.lastname || ''),
        },
      ]}
    >
      {
        !isCreateMode &&
        <div className='user-detail-page-stats'>
          {
            statsItems.map((item, index) => (
              <StatsTile
                key={index}
                isLoading={
                  userDetailStatsContext.status === ContextStatus.LOADING
                }
                item={item}
                isConfidential={false}
              />
            ))
          }
        </div>
      }
      {isCreateMode ? (
        plansContext.status !== ContextStatus.SUCCESS ? (
          <Card loading />
        ) : (
          <UserCreationFormProvider>
            <UserCreationForm />
          </UserCreationFormProvider>
        )
      ) : selectionMismatch ||
        usersContext.selected == null ||
        plansContext.status !== ContextStatus.SUCCESS ? (
        <Card loading />
      ) : (
        <UserEditionFormProvider>
          <UserTabs selected={usersContext.selected} />
        </UserEditionFormProvider>
      )}
    </PageScaffold>
  );
};

export default UserDetailPage;
